import { z } from "zod";

/**
 * SelfContractNodeSchema - Content Schema for Self-Contract Investment Node
 *
 * Self-Contract nodes represent pre-commitment contracts for future trading decisions.
 * Supports two templates:
 * - value-oriented: Long-term value investing
 * - trading-oriented: Short-medium term swing trading
 *
 * Field naming convention:
 * - ticker: Stock ticker symbol
 * - template: value-oriented | trading-oriented
 * - expiration: Contract expiration date (YYYY-MM-DD)
 * - idea: Investment thesis/strategy
 * - trigger: Trigger conditions (nested object)
 * - action: Action to take when triggered
 * - status: active | triggered | cancelled | expired
 */
export const SelfContractNodeSchema = z.object({
  // Protocol metadata
  type: z.literal("self-contract"),
  version: z.literal(1),
  key: z.string(),

  // Core business data
  ticker: z.string().min(1).max(20),
  template: z.enum(["value-oriented", "trading-oriented"]),
  expiration: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // YYYY-MM-DD
  idea: z.string(),
  trigger: z
    .object({
      type: z.enum(["price", "expiration", "time", "custom"]),
      priceLevel: z.number().optional(),
      operator: z.enum([">=", "<=", "==", ">", "<"]).optional(),
      notes: z.string().optional(),
    })
    .strict(),
  action: z.string(),
  status: z.enum(["active", "triggered", "cancelled", "expired"]),

  // Metadata
  createdAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/).optional(),
  updatedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/).optional(),
}).strict();

export type SelfContractNode = z.infer<typeof SelfContractNodeSchema>;
